import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Season } from "../types";
import { apiRequest } from "../config/api";

interface SeasonContextType {
  activeSeason: Season | null;
  selectedSeason: Season | null;
  allSeasons: Season[];
  setSelectedSeason: (seasonId: string | null) => void;
  refreshSeasons: () => Promise<void>;
  isLoading: boolean;
}

const SeasonContext = createContext<SeasonContextType | undefined>(undefined);

interface SeasonProviderProps {
  children: ReactNode;
}

export function SeasonProvider({ children }: SeasonProviderProps) {
  const [activeSeason, setActiveSeason] = useState<Season | null>(null);
  const [selectedSeason, setSelectedSeasonState] = useState<Season | null>(
    null,
  );
  const [allSeasons, setAllSeasons] = useState<Season[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch all seasons
  const fetchSeasons = async () => {
    try {
      setIsLoading(true);
      const response = await apiRequest("/api/seasons");
      if (response.ok) {
        const seasons: Season[] = await response.json();

        // Convert date strings to Date objects
        const seasonsWithDates = seasons.map((season) => ({
          ...season,
          startDate: new Date(season.startDate),
          endDate: new Date(season.endDate),
          createdAt: new Date(season.createdAt),
        }));

        setAllSeasons(seasonsWithDates);

        // Find active season
        const active = seasonsWithDates.find((s) => s.isActive);
        setActiveSeason(active || null);
        console.log('[SeasonContext] Active season:', active?.name, active?.id);

        // Check for saved selected season in localStorage
        const savedSeasonId = localStorage.getItem(
          "padelLeague_selectedSeason",
        );
        console.log('[SeasonContext] Saved season from localStorage:', savedSeasonId);
        if (savedSeasonId) {
          const savedSeason = seasonsWithDates.find(
            (s) => s.id === savedSeasonId,
          );
          if (savedSeason) {
            console.log('[SeasonContext] Using saved season:', savedSeason.name, savedSeason.id);
            setSelectedSeasonState(savedSeason);
          } else {
            // Saved season not found, default to active
            console.log('[SeasonContext] Saved season not found, defaulting to active');
            setSelectedSeasonState(active || null);
            if (active) {
              localStorage.setItem("padelLeague_selectedSeason", active.id);
            }
          }
        } else {
          // No saved season, default to active
          console.log('[SeasonContext] No saved season, defaulting to active');
          setSelectedSeasonState(active || null);
          if (active) {
            localStorage.setItem("padelLeague_selectedSeason", active.id);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching seasons:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch seasons on mount
  useEffect(() => {
    fetchSeasons();
  }, []);

  // Set selected season and save to localStorage
  const setSelectedSeason = (seasonId: string | null) => {
    console.log('[SeasonContext] setSelectedSeason called with:', seasonId);
    if (seasonId === null) {
      // "All Seasons" selected
      setSelectedSeasonState(null);
      localStorage.removeItem("padelLeague_selectedSeason");
      console.log('[SeasonContext] Selected season set to null (All Seasons)');
    } else {
      const season = allSeasons.find((s) => s.id === seasonId);
      if (season) {
        console.log('[SeasonContext] Selected season set to:', season.name, season.id);
        setSelectedSeasonState(season);
        localStorage.setItem("padelLeague_selectedSeason", seasonId);
      }
    }
  };

  // Refresh seasons (call after creating/updating seasons)
  const refreshSeasons = async () => {
    await fetchSeasons();
  };

  const value: SeasonContextType = {
    activeSeason,
    selectedSeason,
    allSeasons,
    setSelectedSeason,
    refreshSeasons,
    isLoading,
  };

  return (
    <SeasonContext.Provider value={value}>{children}</SeasonContext.Provider>
  );
}

export function useSeason() {
  const context = useContext(SeasonContext);
  if (context === undefined) {
    throw new Error("useSeason must be used within a SeasonProvider");
  }
  return context;
}
